import React from "react";

const inputCls = "border hairline px-3 py-2 text-sm w-full focus:outline-none focus:border-stone-900";
const pill = (active) =>
  `mono-label text-[11px] px-3 py-2 border transition-colors ${active ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-700 hover:border-stone-900"}`;

function PillGroup({ options, value, onChange, testPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(([v, label]) => (
        <button key={v} type="button" data-testid={`${testPrefix}-${v}`} onClick={() => onChange(v)}
                className={pill(value === v)} style={value === v ? { color: "#fff" } : undefined}>
          {label}
        </button>
      ))}
    </div>
  );
}

export function BookingTypeToggle({ value, onChange }) {
  return (
    <section>
      <p className="mono-label text-[11px] text-stone-600 mb-2">BOOKING TYPE</p>
      <PillGroup testPrefix="booking-type" value={value} onChange={onChange}
                 options={[["standard", "Standard Appointment"], ["model", "Model Day"]]} />
    </section>
  );
}

export function estimateDeposit(cart, treatments) {
  return cart.reduce((acc, c) => {
    const t = treatments.find((x) => x.id === c.treatment_id);
    if (!t) return acc;
    const qty = Math.max(1, c.quantity || 1);
    const dv = Number(t.deposit_value || 0);
    const dt = t.deposit_type || "fixed";
    if (dt === "fixed") return acc + dv * qty;
    if (dt === "percent") return acc + Number(t.price) * qty * (dv / 100);
    if (dt === "full") return acc + Number(t.price) * qty;
    return acc;
  }, 0);
}

export function PaymentSection({ bookingType, method, setMethod, cashOption, setCashOption, cashAmount, setCashAmount, defaultDeposit }) {
  const isModel = bookingType === "model";
  const showCash = isModel || method === "cash";
  const cashOptions = isModel
    ? [["deposit", "Cash Deposit"], ["full", "Cash Paid in Full"], ["none", "No Payment Required"]]
    : [["full", "Paid in full"], ["deposit", "Deposit paid"], ["none", "Not paid yet"]];
  return (
    <section data-testid="payment-section">
      <p className="mono-label text-[11px] text-stone-600 mb-2">PAYMENT</p>
      {!isModel && (
        <PillGroup testPrefix="payment-method" value={method} onChange={setMethod}
                   options={[["card", "Card Payment (Stripe link)"], ["cash", "Cash Payment"]]} />
      )}
      {showCash && (
        <div className={isModel ? "" : "mt-3"}>
          <PillGroup testPrefix="cash-option" value={cashOption} onChange={setCashOption} options={cashOptions} />
          {cashOption === "deposit" && (
            <label className="block mt-3 max-w-xs">
              <span className="mono-label text-[11px] text-stone-600 block mb-1">DEPOSIT TAKEN (£)</span>
              <input data-testid="cash-deposit-amount" type="number" min="0" step="0.01" value={cashAmount}
                     onChange={(e) => setCashAmount(e.target.value)}
                     placeholder={`Normal deposit · £${Number(defaultDeposit || 0).toFixed(2)}`} className={inputCls} />
            </label>
          )}
          <p className="text-[11px] text-stone-500 mt-2">No card payment is taken. Confirmation and consent form emails are sent to the client straight away.</p>
        </div>
      )}
      {!showCash && (
        <p className="text-[11px] text-stone-500 mt-2">A Stripe deposit link is emailed to the client. Consent is sent once they pay.</p>
      )}
    </section>
  );
}

export function ModelDayFields({ days, dayId, setDayId, slotId, onPickSlot, treatments, cart, onTreatment, time, setTime, duration, setDuration, modelPrice, setModelPrice }) {
  const day = days.find((d) => d.id === dayId);
  const freeSlots = (day?.slots || []).filter((s) => s.status === "AVAILABLE");
  return (
    <section className="space-y-3" data-testid="model-day-fields">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="mono-label text-[11px] text-stone-600 block mb-1">MODEL DAY</span>
          <select data-testid="model-day-select" value={dayId} onChange={(e) => setDayId(e.target.value)} className={inputCls}>
            <option value="">{days.length ? "Select Model Day…" : "No upcoming Model Days"}</option>
            {days.map((d) => <option key={d.id} value={d.id}>{d.date}{d.title ? ` · ${d.title}` : ""}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mono-label text-[11px] text-stone-600 block mb-1">SLOT</span>
          <select data-testid="model-slot-select" value={slotId} onChange={(e) => onPickSlot(e.target.value)} disabled={!day} className={inputCls}>
            <option value="">Custom time</option>
            {freeSlots.map((s) => (
              <option key={s.id} value={s.id}>{s.time} · {s.treatment_name} · £{Number(s.model_price || 0).toFixed(2)}</option>
            ))}
          </select>
        </label>
      </div>
      {day && (
        <>
          <label className="block">
            <span className="mono-label text-[11px] text-stone-600 block mb-1">TREATMENT</span>
            <select data-testid="model-treatment-select" value={cart[0]?.treatment_id || ""} onChange={(e) => onTreatment(e.target.value)} className={inputCls}>
              <option value="">Select treatment…</option>
              {treatments.map((t) => <option key={t.id} value={t.id}>{t.name}{t.price ? ` — £${t.price}` : ""}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="mono-label text-[11px] text-stone-600 block mb-1">TIME</span>
              <input data-testid="model-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} />
            </label>
            <label className="block">
              <span className="mono-label text-[11px] text-stone-600 block mb-1">DURATION (MIN)</span>
              <input data-testid="model-duration" type="number" min="5" value={duration} onChange={(e) => setDuration(e.target.value)} className={inputCls} />
            </label>
            <label className="block">
              <span className="mono-label text-[11px] text-stone-600 block mb-1">MODEL PRICE (£)</span>
              <input data-testid="model-price" type="number" min="0" step="0.01" value={modelPrice} onChange={(e) => setModelPrice(e.target.value)} className={inputCls} />
            </label>
          </div>
          <p className="text-[11px] text-stone-500">Price applies to this booking only — the main treatment price is not changed.</p>
        </>
      )}
    </section>
  );
}
